import {hasQuotePermission} from '../../../../utils/quote-access';
import {Component, inject, input, OnInit, ViewEncapsulation, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {initFlowbite} from "flowbite";
import {ActivatedRoute} from "@angular/router";
import {QuoteService} from '../../../../services/quote.service';
import {SessionService} from "../../../../services/session.service";
import {Permissions} from "../../../../enums/permissions";
import {QuoteButtonType} from "../../../../enums/QuoteButtonType";
import {FormControl, FormGroup, FormBuilder, Validators} from '@angular/forms';

@Component({
  selector: 'quote-approve-popup',
  templateUrl: './quote-approve-popup.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./quote-approve-popup.component.css'],
  standalone: false
})
export class QuoteApprovePopupComponent implements OnInit {
  status = input<string>('0');
  deselectedItems = input<Set<number>>(new Set());
  quoteLineItems = input<any[]>([]);
  poNumber: any = '';
  type: QuoteButtonType = QuoteButtonType.Approve;
  quoteId: any;
  changesNeeded: string = '';
  reasonDecline: string = '';
  currentUser: any;
  attachments: any[] = [];
  isLoading: boolean = false;
  po_number_required: boolean = false;
  po_attachments_required: boolean = false;

  po_number_requiredControl = new FormControl('', this.po_number_required ? Validators.required : null);
  po_attachments_requiredControl = new FormControl('', this.po_attachments_required ? Validators.required : null);

  protected readonly Permission = Permissions;
  protected readonly QuoteButtonType = QuoteButtonType;
  private readonly quoteService = inject(QuoteService);
  private readonly router = inject(ActivatedRoute);
  private readonly sessionService = inject(SessionService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  public readonly masterFormGroup: FormGroup = this.formBuilder.group({});

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser = res.user);
  }

  hasPermission(permission: string): any {
    return hasQuotePermission(this.currentUser, permission);
  }

  async isPoRequiredCheck() {
    if (this.currentUser && 'is_po_required' in this.currentUser) {
      const is_po_required = this.currentUser.is_po_required;
      if (is_po_required === '2') { //Require PO Number
        this.po_number_required = true;
        this.masterFormGroup.addControl('po_number_requiredControl', this.po_number_requiredControl);

      } else if (is_po_required === '3') { // Require PO Number and PO Attachment
        this.po_number_required = true;
        this.po_attachments_required = true;
        this.masterFormGroup.addControl('po_number_requiredControl', this.po_number_requiredControl);
        this.masterFormGroup.addControl('po_attachments_requiredControl', this.po_attachments_requiredControl);
      }
    }
  }

  ngOnInit(): void {
    initFlowbite();
    this.isPoRequiredCheck()

    this.router.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (t: any) => {
        this.quoteId = t['quote_id'];
      }
    });
  }

  getTitle() {
    if (this.type == QuoteButtonType.Approve) {
      return 'Approve Quote';
    } else if (this.type == QuoteButtonType.Decline) {
      return 'Decline a Quote';
    } else {
      return 'Request Changes';
    }
  }

  getButtonText() {
    if (this.type == QuoteButtonType.Approve) {
      return 'Approve';
    } else if (this.type == QuoteButtonType.Decline) {
      return 'Decline';
    } else {
      return 'Request Changes';
    }
  }

  getButtonColor() {
    if (this.type == QuoteButtonType.Approve) {
      return 'btn-accept';
    } else if (this.type == QuoteButtonType.Decline) {
      return 'btn-decline';
    } else {
      return 'btn-ask-change';
    }
  }

  getButtonDisable() {
    if (this.type == QuoteButtonType.Approve) {
      return false;
    } else if (this.type == QuoteButtonType.Decline) {
      return false;
    } else if (this.type == QuoteButtonType.RequestChanges) {
      return this.isNullOrEmpty(this.changesNeeded);
    } else {
      return true;
    }
  }

  handleButtonClick(action: QuoteButtonType): void {
    this.quoteId ??= this.router.snapshot.queryParamMap.get('quote_id');
    this.type = action;
  }

  getDeselectedItemsArray(): Array<{quoteLineItemId: number, isMandatory: boolean}> {
    // Return only deselected recommended items (to be deleted from quote)
    return Array.from(this.deselectedItems()).map(id => ({
      quoteLineItemId: id,
      isMandatory: false
    }));
  }

  approveQuote() {
    if (this.validateForm(this.masterFormGroup)) {
      this.isLoading = true;
      this.closeModal();
      this.quoteService.ApproveQuote(this.quoteId,
        {
          "poNumber": this.poNumber,
          "attachments": this.attachments,
          "lineItemMandatoryChanges": this.getDeselectedItemsArray()
        }).subscribe({
        next: () => {
        },
        complete: () => {
          this.reloadPage()
        }
      })
    }
  }

  declineQuote() {
    this.quoteService.DeclineQuote(this.quoteId,
      {
        "reasonForDecline": this.reasonDecline,
      }).subscribe({
      next: () => {
      },
      complete: () => {
        this.reloadPage()
      }
    })
  }

  requestChanges() {
    this.quoteService.RequestChanges(this.quoteId,
      {
        "changesNeeded": this.changesNeeded,
        "lineItemMandatoryChanges": this.getDeselectedItemsArray()
      }).subscribe({
      next: () => {
      },
      complete: () => {
        this.reloadPage()
      }
    })
  }

  handleSendClick(): void {
    if (this.type == QuoteButtonType.Approve) {
      this.approveQuote();
    }
    if (this.type == QuoteButtonType.Decline) {
      this.declineQuote();
    }
    if (this.type == QuoteButtonType.RequestChanges) {
      this.requestChanges();
    }
  }

  reloadPage() {
    window.location.reload();
  }

  closeModal() {
    const modalElement = document.getElementById('defaultModal');
    if (modalElement) {
      modalElement.classList.add('hidden');
    }
  }

  isNullOrEmpty(valueString: any): boolean {
    return valueString == '' || valueString == null;
  }

  onPhotoSelected(event: any): void {
    const files: FileList = event.target.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = ((file) => {
        return (e: any) => {
          this.attachments.push({content: e.target.result, fileName: file.name});
        };
      })(file);
      reader.readAsDataURL(file);
    }
  }

  isSupportedFileType(fileName: string): boolean {   //Check photo type for correct displayed
    const supportedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'psd', 'ai', 'eps'];
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return !!fileExtension && supportedFileTypes.includes(fileExtension);
  }

  removePhoto(index: number): void {
    this.attachments.splice(index, 1);
  }

  validateForm(form: FormGroup): boolean {
    if (!form.valid) {
      for (let i in form.controls) {
        form.controls[i].markAsTouched()
      }
      return false
    } else {
      return true
    }
  }
}
